import React, { useEffect, useState, useRef } from "react";
import { bool, shape, arrayOf } from "prop-types";
import { push, replace } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import * as numberUtils from "util/number";
import { connect, useDispatch } from "react-redux";
import { selectors as environmentSelectors, actions as environmentActions, STATUS } from "reducers/environment";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { actions as notificationActions } from "reducers/notification";
import { actions as modalActions } from "reducers/modal";
import TextField from "pages/_components/fields/TextField";
import Notification from "pages/_components/Notification";
import Checkbox from "pages/_components/Checkbox";
import MainContainer from "pages/_components/MainContainer";
import Head from "pages/_components/Head";
import Selector from "pages/_components/fields/formik/Selector";
import Button from "pages/_components/Button";
import CustomText from "pages/_components/CustomText/CustomText";
import I18n from "pages/_components/I18n";
import ErrorPopup from "pages/_components/ErrorPopup";
import HeaderSimple from "pages/_components/HeaderSimple/HeaderSimple";
import { isIOS } from "react-device-detect";
import Uploader from "../_components/Uploader";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import EnvironmentBeneficiarySlide from "../_components/EnvironmentBeneficiarySlide";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import { useBeneficiaryDocumentCode, useBeneficiarySelectors, useBeneficiaries, useVerifyPerson } from "./hooks";
import { useHistoricData } from "../hooks/useHistoricData";

const FORM_ID = "environment.step.beneficiaries";
const PAGE_ID = "StepBeneficiaries";

const StepBeneficiaries = ({
    isMobile,
    fetching,
    fetchingLocalities,
    stateList,
    localityList,
    beneficiariesRequest,
    beneficiariesBackup,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const { status: beneficiariesRequestStatus, error: beneficiariesRequestError } = beneficiariesRequest;
    const [error, setError] = useState("");
    const [selectedShareholders, setSelectedShareholders] = useState([]);
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const { beneficiariesDocumentCode } = useBeneficiaryDocumentCode();
    const [capitalBeneficiaryMaxLength, setCapitalBeneficiaryMaxLength] = useState(5);
    const [capitalBeneficiaryMinValue, setCapitalBeneficiaryMinValue] = useState(10);
    const [capitalPercentajeTemp, setCapitalPercentageTemp] = useState([]);
    const [finalControlTemp, setFinalControlTemp] = useState([]);

    const {
        beneficiariesSelectors,
        beneficiarySliderIsVisible,
        closeSlide,
        setGetBeneficiarySliderSelectors,
    } = useBeneficiarySelectors();

    const {
        shareholders,
        addFinalBeneficiaries,
        setAddFinalBeneficiaries,
        setAlreadyFetchShareholders,
    } = useBeneficiaries();

    const { tempBeneficiary, setTempBeneficiary, verifyPersonRequestStatus, verifyBeneficiary } = useVerifyPerson();

    useEffect(() => {
        dispatch(onboardingActions.listPersonalInfoRequest(false));

        closeSlide();
        const maxLength = configUtils.getInteger("onboarding.capitalHolding.maxLength", 5);
        setCapitalBeneficiaryMaxLength(maxLength);

        const minValue = configUtils.getInteger("onboarding.finalBeneficiary.capitalHolding.min", 10);
        setCapitalBeneficiaryMinValue(minValue);
    }, []);

    useEffect(() => {
        if (beneficiariesBackup?.hasSwornDeclaration !== undefined) {
            if (!beneficiariesBackup?.hasSwornDeclaration) {
                setAddFinalBeneficiaries(beneficiariesBackup?.hasSwornDeclaration === "false");
            }
            formRef.current.setFieldValue(
                "hasSwornDeclaration",
                beneficiariesBackup?.hasSwornDeclaration ? "true" : "false",
            );
        } else {
            setAddFinalBeneficiaries(false);
        }
    }, [beneficiariesBackup?.hasSwornDeclaration]);

    useEffect(() => {
        if (beneficiariesBackup?.finalBeneficiaries.length > 0) {
            setSelectedShareholders(beneficiariesBackup?.finalBeneficiaries);
        }
    }, [beneficiariesBackup?.finalBeneficiaries]);

    useEffect(() => {
        const capitalTempArray = Array.from(capitalPercentajeTemp);

        if (capitalTempArray.length === 0) {
            shareholders.forEach((sh, index) => {
                formRef.current.setFieldValue(`capitalBeneficiaryPercentage-${index}`, sh.capitalHolding);
                capitalTempArray.push({
                    document: sh?.documentNumber,
                    value: sh?.capitalHolding,
                });
            });
        }

        if (beneficiariesBackup?.finalBeneficiaries?.length > 0 && shareholders?.length > 0) {
            const finalControlTempArray = Array.from(finalControlTemp);

            beneficiariesBackup.finalBeneficiaries.forEach((finalBeneficiary) => {
                const index = shareholders.findIndex(
                    (shareholder) => shareholder?.documentNumber === finalBeneficiary.documentNumber,
                );
                formRef.current.setFieldValue(`shareholder-${index}`, true);
                formRef.current.setFieldValue(
                    `capitalBeneficiaryPercentage-${index}`,
                    finalBeneficiary.capitalHoldingBeneficiary,
                );
                formRef.current.setFieldValue(
                    `finalControl-${index}`,
                    finalBeneficiary.finalControl ? "true" : "false",
                );

                const idxCapTempArray = capitalTempArray.findIndex(
                    (e) => e && e.document === finalBeneficiary?.documentNumber,
                );
                if (capitalTempArray) {
                    capitalTempArray.splice(idxCapTempArray, 1, {
                        document: finalBeneficiary?.documentNumber,
                        value: finalBeneficiary?.capitalHoldingBeneficiary,
                    });
                }

                if (
                    finalControlTempArray &&
                    finalControlTempArray.findIndex((e) => e && e.document === finalBeneficiary?.documentNumber) === -1
                ) {
                    finalControlTempArray.push({
                        document: finalBeneficiary?.documentNumber,
                        value: finalBeneficiary?.finalControl,
                    });
                }
            });
            setFinalControlTemp(finalControlTempArray);
        }
        setCapitalPercentageTemp(capitalTempArray);
    }, [shareholders, beneficiariesBackup?.finalBeneficiaries]);

    const noBackupData = beneficiariesBackup?.hasSwornDeclaration === undefined;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    useEffect(() => {
        if (beneficiariesRequestStatus === STATUS.SUCCESS) {
            dispatch(environmentActions.resetBeneficiariesRequest());
            dispatch(push("/environment/stepDocumentation"));
            getBackupDataByStepId("stepDocumentation");
        } else if (beneficiariesRequestStatus === STATUS.FAILURE) {
            dispatch(push("/environment/stepError"));
        }
    }, [beneficiariesRequestStatus, beneficiariesRequestError]);

    const onChangeCheckbox = (newVal, shareholder) => {
        const temp = Array.from(selectedShareholders);
        if (newVal) {
            temp.push(shareholder);
        } else {
            const idx = selectedShareholders.findIndex((s) => s?.documentNumber === shareholder?.documentNumber);
            temp.splice(idx, 1);
        }
        setSelectedShareholders(temp);
    };

    const onChangeCapitalPercentage = (idx, newVal, shareholder) => {
        const { value } = newVal.target;
        const temp = Array.from(capitalPercentajeTemp);
        if (value.length === 0) {
            const index = temp.findIndex((e) => e.document === shareholder.documentNumber);

            if (index !== -1) {
                temp.splice(index, 1);
                setCapitalPercentageTemp(temp);
            }
            formRef.current.setFieldError(`capitalBeneficiaryPercentage-${idx}`, null);
        } else if (numberUtils.toNumber(value, ".", 2) < numberUtils.toNumber(shareholder?.capitalHolding, ".", 2)) {
            formRef.current.setFieldError(
                `capitalBeneficiaryPercentage-${idx}`,
                i18nUtils.get(
                    "onboarding.finalBeneficiary.capitalHolding.error",
                    // eslint-disable-next-line max-len
                    "El porcentaje de capital como Beneficiario final, debe ser mayor o igual que el porcentaje como Socio",
                ),
            );
        } else {
            formRef.current.setFieldError(`capitalBeneficiaryPercentage-${idx}`, null);
            const elem = { document: shareholder?.documentNumber, value };

            const index = temp.findIndex((e) => e.document === elem.document);

            if (index !== -1) {
                temp.splice(index, 1, elem);
            } else {
                temp.push(elem);
            }
            setCapitalPercentageTemp(temp);
        }
    };

    const onChangeFinalControl = (idx, newVal, shareholder) => {
        const temp = Array.from(finalControlTemp);
        if (newVal) {
            const value = newVal === "true";
            const elem = { document: shareholder?.documentNumber, value };
            const index = temp.findIndex((e) => e.document === elem.document);

            if (index !== -1) {
                temp.splice(index, 1, elem);
            } else {
                temp.push(elem);
            }
            setFinalControlTemp(temp);
        }
    };

    useEffect(() => {
        // Si se agrego un beneficiario
        if (shareholders && shareholders.length > 0 && tempBeneficiary !== null) {
            formRef.current.setFieldValue(`shareholder-${shareholders.length - 1}`, true);
            closeSlide();
            onChangeCheckbox(true, shareholders[shareholders.length - 1]);
            setTempBeneficiary(null);

            const newShareholder = shareholders[shareholders.length - 1];

            const { capitalHoldingBeneficiary, finalControl } = newShareholder;

            // Se agrega el capital del nuevo beneficiario al campo en el listado de beneficiarios
            const tempCapital = Array.from(capitalPercentajeTemp);
            tempCapital.push({
                document: newShareholder.documentNumber,
                value: capitalHoldingBeneficiary,
            });
            setCapitalPercentageTemp(tempCapital);
            formRef.current.setFieldValue(
                `capitalBeneficiaryPercentage-${shareholders.length - 1}`,
                capitalHoldingBeneficiary,
            );

            // Se agrega el valor de control final del nuevo beneficiario al campo en el listado de beneficiarios
            const tempFinalControl = Array.from(finalControlTemp);
            tempFinalControl.push({
                document: newShareholder.documentNumber,
                value: finalControl,
            });
            setFinalControlTemp(tempFinalControl);
            formRef.current.setFieldValue(`finalControl-${shareholders.length - 1}`, finalControl);

            dispatch(
                notificationActions.showNotification(
                    i18nUtils.get("", "Confirmado. Se agregó un beneficiario final correctamente"),
                    "success",
                    ["environment"],
                ),
            );
        }
    }, [shareholders]);

    const submitIsAllowed = ({ values }) => {
        const { hasSwornDeclaration, swornDeclarationIDU } = values;
        if (hasSwornDeclaration === "true" && swornDeclarationIDU === "") {
            return false;
        }

        return true;
    };

    const onHeaderBack = () => {
        /*
         * En caso de volver atras en las pantallas se debe dejar en false y resetear la lista de accionistas,
         * por si se modifican los mismos, para que se vuelva a realizar la consulta
         */
        setAlreadyFetchShareholders(false);
        dispatch(environmentActions.resetShareholdersBeneficiaries());

        getBackupDataByStepId("stepProxies");
        dispatch(replace("/environment/stepProxies"));
    };

    const REQUIRED_TEXT = i18nUtils.get("global.error.required");
    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            hasSwornDeclaration: beneficiariesBackup?.hasSwornDeclaration
                ? " true"
                : !beneficiariesBackup?.hasSwornDeclaration && "false",
            swornDeclarationIDU: beneficiariesBackup?.swornDeclarationIDU || "",
            swornDeclarationIDUName: beneficiariesBackup?.swornDeclarationIDUName || "",
        },
        validationSchema: () =>
            Yup.object().shape({
                hasSwornDeclaration: Yup.string().required(REQUIRED_TEXT),
                swornDeclarationIDU: Yup.string().notRequired(),
            }),
        onSubmit: (values) => {
            const uniqueDocumentNumbers = {};

            const filteredData = selectedShareholders.filter((item) => {
                if (!uniqueDocumentNumbers[item.documentNumber]) {
                    uniqueDocumentNumbers[item.documentNumber] = true;
                    return true;
                }

                return false;
            });

            let validationFinalControlRequired = true;
            let validationCapitalRequired = true;
            let validationCapitalMinValue = true;
            let totalCapital = 0;

            let finalBeneficiariesData = filteredData.map((obj) => {
                const idx = finalControlTemp.findIndex((e) => e && e.document === obj.documentNumber);
                if (idx !== -1 && finalControlTemp[idx] !== "" && finalControlTemp[idx] !== undefined) {
                    return { ...obj, finalControl: finalControlTemp[idx].value };
                }
                validationFinalControlRequired = false;
                return obj;
            });

            if (validationFinalControlRequired) {
                finalBeneficiariesData = finalBeneficiariesData.map((obj) => {
                    const idx = capitalPercentajeTemp.findIndex((e) => e && e.document === obj.documentNumber);
                    if (idx !== -1 && capitalPercentajeTemp[idx] !== "" && capitalPercentajeTemp[idx] !== undefined) {
                        return { ...obj, capitalHoldingBeneficiary: capitalPercentajeTemp[idx].value };
                    }
                    validationCapitalRequired = false;
                    return obj;
                });

                if (validationCapitalRequired) {
                    finalBeneficiariesData.forEach((f) => {
                        if (f.capitalHoldingBeneficiary !== null) {
                            const capitalValue = numberUtils.toNumber(f.capitalHoldingBeneficiary, ".", 2);
                            if (capitalValue < capitalBeneficiaryMinValue) {
                                validationCapitalMinValue = false;
                            } else {
                                totalCapital += capitalValue;
                            }
                        }
                    });
                }
            }

            if (!validationFinalControlRequired) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            "onboarding.finalBeneficiary.finalControl.required.error",
                            "Debe establecer si cada beneficiario requiere control final",
                        ),
                        "error",
                        ["environment"],
                    ),
                );
            } else if (!validationCapitalRequired) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            "onboarding.finalBeneficiary.capitalHolding.required.error",
                            "El porcentaje de tenencia de cada Beneficiario final es requerido",
                        ),
                        "error",
                        ["environment"],
                    ),
                );
            } else if (!validationCapitalMinValue) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            "onboarding.finalBeneficiary.capitalHolding.minValue.error",
                            // eslint-disable-next-line max-len
                            `El capital de tenencia de cada Beneficiario final debe ser de mínimo ${capitalBeneficiaryMinValue}%`,
                            { MIN_VALUE: capitalBeneficiaryMinValue },
                        ),
                        "error",
                        ["environment"],
                    ),
                );
            } else if (finalBeneficiariesData.length > 0 && (totalCapital > 100 || totalCapital < 1)) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            "onboarding.finalBeneficiaries.totalCapitalPercentaje.error",
                            // eslint-disable-next-line max-len
                            "La sumatoria de los porcentajes de tenencia de todos los beneficiarios finales tiene que ser menor o igual a 100 y mayor o igual a 1",
                        ),
                        "error",
                        ["environment"],
                    ),
                );
            } else if (values) {
                dispatch(
                    environmentActions.setBeneficiariesBackup({
                        hasSwornDeclaration: values?.hasSwornDeclaration === "true",
                        swornDeclarationIDU: values?.swornDeclarationIDU,
                        swornDeclarationIDUName: values?.swornDeclarationIDUName,
                        finalBeneficiaries: finalBeneficiariesData,
                    }),
                );
                dispatch(
                    environmentActions.saveBeneficiariesRequest({
                        hasSwornDeclaration: values?.hasSwornDeclaration === "true",
                        swornDeclarationIDU: values?.swornDeclarationIDU,
                        swornDeclarationIDUName: values?.swornDeclarationIDUName,
                        finalBeneficiaries: finalBeneficiariesData,
                    }),
                );
            }
        },
    };

    const onChangeHasSwornDeclaration = (value, allFormik) => {
        setAddFinalBeneficiaries(value === "true");
        allFormik.setFieldValue("hasSwornDeclaration", value);
    };

    const handleOnChangeCountry = (countryCode) => {
        if (stateList.length > 0) {
            dispatch(environmentActions.cleanListStates());
        }
        if (localityList.length > 0) {
            dispatch(onboardingActions.cleanLocalityList());
        }
        dispatch(environmentActions.listStatesRequest(countryCode));
    };

    const handleOnChangeState = (countryCode, provinceCode) => {
        if (localityList.length > 0) {
            dispatch(onboardingActions.cleanLocalityList());
        }
        dispatch(onboardingActions.listLocalityRequest(countryCode, provinceCode));
    };

    const itemIsDisabled = (shareholder) =>
        selectedShareholders.length === 0 ||
        selectedShareholders.findIndex((s) => s?.documentNumber === shareholder?.documentNumber) === -1;

    return (
        <>
            {isMobile ? (
                <Head
                    titleText={i18nUtils.get("legalPerson.title", "Registro")}
                    onBack={onHeaderBack}
                    hideNavbarInMobile
                    showCenterContent
                    tooltipMobile
                    onClickTooltip={() => setHelpIsVisible(true)}
                    tooltipImage={isIOS ? "images/figma/icon/help-white.svg" : "images/figma/icon/help.svg"}
                />
            ) : (
                <HeaderSimple inverse />
            )}
            <Notification scopeToShow="environment" />
            <MainContainer className="onboarding" showLoader={beneficiariesRequestStatus === STATUS.FETCHING}>
                <EnvironmentStepper isMobile={isMobile} page="StepBeneficiaries" />
                <div className="step">
                    {isMobile && (
                        <I18n
                            id="environment.beneficiaries.title"
                            defaultValue="Beneficiarios Finales"
                            component="h1"
                        />
                    )}
                    {!isMobile && (
                        <Head
                            titleText={i18nUtils.get("", "Beneficiarios Finales")}
                            onBack={onHeaderBack}
                            tooltip
                            showCenterContent
                            onClickTooltip={() => setHelpIsVisible(true)}
                            tooltipImage="images/figma/icon/help.svg"
                        />
                    )}
                    <Formik {...formikProps} ref={formRef}>
                        {(allFormik) => (
                            <Form>
                                <div className="grid-table grid-table-form">
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <I18n
                                                id=""
                                                defaultValue={i18nUtils.get(
                                                    "",
                                                    "¿Posee Declaración Jurada con estructura y Beneficiarios Finales?",
                                                )}
                                                component="label"
                                            />
                                            <Field
                                                component={Selector}
                                                renderAs="radio"
                                                inLineControl
                                                idForm={FORM_ID}
                                                name="hasSwornDeclaration"
                                                options={[
                                                    {
                                                        id: "true",
                                                        label: i18nUtils.get("global.yes", "Sí"),
                                                    },
                                                    {
                                                        id: "false",
                                                        label: i18nUtils.get("global.no", "No"),
                                                    },
                                                ]}
                                                onChange={(selected) =>
                                                    onChangeHasSwornDeclaration(selected, allFormik)
                                                }
                                            />
                                        </div>
                                        {addFinalBeneficiaries && allFormik.values.hasSwornDeclaration === "true" && (
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <Field
                                                    component={Uploader}
                                                    idForm={FORM_ID}
                                                    name="swornDeclarationIDU"
                                                    label="Agregá documentación"
                                                    extensionDocument="pdf"
                                                    documentCode={beneficiariesDocumentCode}
                                                    defaultDocument={{
                                                        documentId: beneficiariesBackup?.swornDeclarationIDU,
                                                        documentName: beneficiariesBackup?.swornDeclarationIDUName,
                                                    }}
                                                    withDocumentName
                                                />
                                            </div>
                                        )}

                                        {addFinalBeneficiaries !== null &&
                                            !addFinalBeneficiaries &&
                                            allFormik.values.hasSwornDeclaration === "false" && (
                                                <div className="grid-table-data col-start-2 col-span-10">
                                                    <I18n
                                                        id=""
                                                        defaultValue={i18nUtils.get(
                                                            "",
                                                            "Seleccioná cuales socios son beneficiarios finales",
                                                        )}
                                                    />
                                                    <div className="two-columns">
                                                        {shareholders != null && shareholders.length > 0
                                                            ? shareholders.map((shareholder, index) => (
                                                                  <div className="box box-shadow transparent">
                                                                      <Field
                                                                          component={Checkbox}
                                                                          idForm={FORM_ID}
                                                                          name={`shareholder-${index}`}
                                                                          hideLabel
                                                                          onChange={(v) =>
                                                                              onChangeCheckbox(v, shareholder)
                                                                          }
                                                                      />
                                                                      <div>
                                                                          <I18n
                                                                              id=""
                                                                              defaultValue={i18nUtils.get(
                                                                                  "",
                                                                                  "CUIL del socio",
                                                                              )}
                                                                          />
                                                                          <p>{shareholder?.documentNumber}</p>
                                                                          <Field
                                                                              component={TextField}
                                                                              idForm={FORM_ID}
                                                                              name={`capitalBeneficiaryPercentage-${index}`}
                                                                              maxLength={capitalBeneficiaryMaxLength}
                                                                              labelTextDefault={i18nUtils.get(
                                                                                  "onboarding.finalBeneficiary.capitalPercentaje.label",
                                                                                  "% tenencia de Beneficiario final",
                                                                              )}
                                                                              handleOnChange={(v) =>
                                                                                  onChangeCapitalPercentage(
                                                                                      index,
                                                                                      v,
                                                                                      shareholder,
                                                                                  )
                                                                              }
                                                                              hidePlaceholder
                                                                              isDouble
                                                                              autoFocus
                                                                              disabled={itemIsDisabled(shareholder)}
                                                                          />
                                                                          <div>
                                                                              <I18n
                                                                                  id=""
                                                                                  defaultValue={i18nUtils.get(
                                                                                      "",
                                                                                      "¿Ejerce control final?",
                                                                                  )}
                                                                              />

                                                                              <Field
                                                                                  component={Selector}
                                                                                  renderAs="radio"
                                                                                  idForm={FORM_ID}
                                                                                  name={`finalControl-${index}`}
                                                                                  options={[
                                                                                      {
                                                                                          id: "true",
                                                                                          label: i18nUtils.get(
                                                                                              "global.yes",
                                                                                              "Sí",
                                                                                          ),
                                                                                      },
                                                                                      {
                                                                                          id: "false",
                                                                                          label: i18nUtils.get(
                                                                                              "global.no",
                                                                                              "No",
                                                                                          ),
                                                                                      },
                                                                                  ]}
                                                                                  onChange={(v) =>
                                                                                      onChangeFinalControl(
                                                                                          index,
                                                                                          v,
                                                                                          shareholder,
                                                                                      )
                                                                                  }
                                                                                  disabled={itemIsDisabled(shareholder)}
                                                                              />
                                                                          </div>
                                                                      </div>
                                                                  </div>
                                                              ))
                                                            : null}
                                                        <div
                                                            aria-hidden="true"
                                                            className="box transparent box-border-dashed last-item"
                                                            onClick={() => setGetBeneficiarySliderSelectors(true)}>
                                                            <Button
                                                                className="btn-secondary icon-medium btn-only-icon enphasize"
                                                                image="plus.svg"
                                                                block={false}
                                                            />
                                                            <Button className="btn-link" block={false}>
                                                                <CustomText
                                                                    text={i18nUtils.get(
                                                                        "",
                                                                        "Agregá otro beneficiario final",
                                                                    )}
                                                                />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <div className="box box-border-soft flex">
                                                <p>
                                                    Si tu beneficiario final no posee documentación argentina, se
                                                    cargará en la etapa de validación.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-table form-actions">
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={
                                                    fetching ||
                                                    beneficiariesRequestStatus === STATUS.FETCHING ||
                                                    verifyPersonRequestStatus === STATUS.FETCHING
                                                }
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>

            <EnvironmentBeneficiarySlide
                countryList={beneficiariesSelectors.countrySelector}
                pepJobList={beneficiariesSelectors.pepJobsSelector}
                pepRelationshipTypeList={beneficiariesSelectors.pepRelationshipTypesSelector}
                onChangeState={handleOnChangeState}
                onChangeCountry={handleOnChangeCountry}
                fetchingStates={fetching}
                fetchingLocalities={fetching || fetchingLocalities}
                stateList={stateList}
                localityList={localityList}
                slideIsVisible={beneficiarySliderIsVisible}
                closeSlide={() => closeSlide()}
                onSubmit={(values) => verifyBeneficiary(values)}
                isMobile={isMobile}
                shareholders={shareholders}
            />
            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />
            <ErrorPopup error={error} handleClick={() => dispatch(modalActions.modalClose())} />
        </>
    );
};

StepBeneficiaries.propTypes = {
    isMobile: bool.isRequired,
    fetching: bool,
    beneficiariesRequest: arrayOf(shape({})).isRequired,
    stateList: arrayOf(shape({})).isRequired,
    localityList: arrayOf(shape({})).isRequired,
    fetchingLocalities: bool.isRequired,
    beneficiariesBackup: shape({}).isRequired,
};

StepBeneficiaries.defaultProps = {
    fetching: false,
};

const mapStateToProps = (state) => ({
    fetching: environmentSelectors.getFetching(state),
    beneficiariesRequest: environmentSelectors.getBeneficiariesRequest(state),
    stateList: environmentSelectors.getStateList(state),
    fetchingLocalities: onboardingSelectors.getFetching(state),
    localityList: onboardingSelectors.getLocalityList(state),
    beneficiariesBackup: environmentSelectors.getBeneficiariesBackup(state),
});

export default connect(mapStateToProps)(StepBeneficiaries);
